import { effect, inject, Injectable, signal } from "@angular/core";
import { ITranslateObject, JobsBillingCard } from "../views-interfaces";
import { MultiSelectModel, Option, type OptionValue } from "@app/filter-menu/filter-types";
import { UsersService, compareByActiveAndName } from "@app/shared";
import { CardsService } from "@services/index";
import { UserDataModel } from "@app/settings-interfaces";

@Injectable()
export class UsersFilterGeneratorService {
    private readonly usersService = inject(UsersService);
    protected readonly cardsService = inject(CardsService);

    protected label: ITranslateObject = {
        "en-CA": "Users",
        "fr-CA": "Utilisateurs",
    };

    /* Optional key in ICardsCount.totalByRole used to decide whether an inactive user should still
     * appear in this filter's dropdown. When undefined (base behaviour), inactive users are shown
     * as long as they still hold at least one card in any role (ICardsCount.total > 0). */
    protected visibilityCountKey?: string;

    public readonly model = signal<MultiSelectModel | null>(null);
    private readonly selection = signal<Option[]>([]);
    private readonly reload = signal<boolean>(true);

    constructor() {
        effect(() => {
            if (!this.reload()) return;
            const selection = this.selection();
            this.model.set(this.generateModel(selection));
        });
    }

    public loadModel(selection: Option[]): void {
        this.reload.set(false);
        this.selection.set(selection ?? []);
        this.reload.set(true);
    }

    public updateSelection(selection: Option[]): void {
        this.selection.set(selection ?? []);
    }

    public generateModel(selection: Option[]): MultiSelectModel {
        const users = Array.from(this.usersService.usersById()?.fromUserId.values() ?? []);
        users.sort(compareByActiveAndName);
        const options: Option[] = users.map((user) => ({
            text: user.FullName ?? `${user.FirstName} ${user.LastName}`,
            value: user.IFirmUserId,
            isVisible: () => this.isUserOptionVisible(user),
        }));

        options.unshift({
            text: "General.None",
            value: "-1",
            isVisible: () => true,
        });

        const applicableOptions = options.filter((option) =>
            option.isVisible ? (typeof option.isVisible === "boolean" ? option.isVisible : option.isVisible(undefined)) : true,
        );

        return {
            label: this.label,
            applicableOptions,
            allOptions: options,
            selection: selection.filter((selected) => applicableOptions.some((option) => selected.value === option.value)),
        } as MultiSelectModel;
    }

    protected isUserOptionVisible(user: UserDataModel): boolean {
        if (user.Active) return true;
        const counts = this.cardsService.getCardsCountForUser(user);
        if (this.visibilityCountKey) {
            return (counts.totalByRole?.[this.visibilityCountKey] ?? 0) > 0;
        }
        return (counts.total ?? 0) > 0;
    }

    public getPrintDescription(
        label: string | ITranslateObject,
        selectedOptions: Option[],
    ): { label: string | ITranslateObject; values: (string | ITranslateObject)[] } | null {
        if (!selectedOptions || selectedOptions.length === 0) {
            return null;
        } else {
            return {
                label,
                values: selectedOptions.map((value) => {
                    return value.text;
                }),
            };
        }
    }

    public generateFilter(selectedValues: OptionValue[]) {
        if (!selectedValues || selectedValues.length === 0) {
            return null;
        } else {
            return (card: JobsBillingCard) => {
                const userId = card.job.currentlyResponsible ? card.job.currentlyResponsible.toString() : null;
                const jobPartner = card.job.partner ? card.job.partner.toString() : null;
                const jobManager = card.job.manager ? card.job.manager.toString() : null;
                const owner = card.job.owner ? card.job.owner.toString() : null;
                const contactPartner = card.contactInformation?.partner ? card.contactInformation.partner.toString() : null;
                const contactManager = card.contactInformation?.manager ? card.contactInformation.manager.toString() : null;

                return selectedValues.some((value) => {
                    if (value === "-1") {
                        return (
                            userId === null ||
                            jobPartner === null ||
                            jobManager === null ||
                            owner === null ||
                            contactPartner === null ||
                            contactManager === null
                        );
                    } else {
                        return (
                            userId === value ||
                            jobManager === value ||
                            jobPartner === value ||
                            owner === value ||
                            contactManager === value ||
                            contactPartner === value
                        );
                    }
                });
            };
        }
    }
}
